// RecoveryGenerationPickerModal — 荒らし復旧 UI 第 1 段階「続きを描く」時点選択
// (vandalism-recovery-ui-2026-09-27.md §2「詳細設計」+ 追補「範囲の修正」+
// 「入口を『続きを描く』に統一」)。
//
// **入口統一 (2026-09-28)**: 別ボタン「時点を選んで戻す」・文書の選択欄・自動探索は
// 廃止。過去キャンバス一覧 (`PastCanvasListModal`) の各行の「続きを描く」/ 先頭の
// 「今の絵」カードが、この画面を**その行の文書**(または現行文書) にスコープして開く。
// 復元は必ずこの比較画面を経由する (取り違え対策が強まる、anti-vandalism-2026-09-24.md
// §1.4)。
//
// - **スライダー**: 単一ハンドル、初期位置は右端 (= 行の保存時点、または「今の絵」
//   なら最新世代)。タイムラプス (`TimelapseShareModal`) と同じ操作感 — 常に有効で
//   端からもドラッグできる (未選択状態は無い)。
// - **世代の絞り込み**: `isSeedCopy` を除外し、行から開いた場合は `seq <=
//   row.snapshotSeq` にも絞る (`buildPickerGenerations`)。行の `snapshotSeq` が
//   世代一覧に無ければ (seed 世代として除外 / R2 に無い 等) 右端に「行そのもの」の
//   合成点を足す。プレビューは既存 `past-canvases/:id/snapshot-urls`、確定は既存
//   `pastCanvasId` で直接 `whiteboard.restore-request` (= POST を経由しない)。
//   世代 API が 404/503 でも「行そのもの」1 点だけで動く (= 従来の「続きを描く」と
//   同じ耐性)。右端 (= 行の保存時点) のラベルは日時ではなく「保存時点」(最終
//   snapshot 以降の描画は含まれないため、精密な時刻に見せない)。
// - **「今の絵」カード**から開いた場合 (`row` 省略) は上限なし・合成点も無し。
//   右端 (= 最新世代) は「今」と同じなので確定しても何も変わらない — この間は
//   確定を無効化し、案内文を出す。
// - **今の絵**と**選んだ時点の絵**を大きく並べて比較する (取り違え対策の要)。
//   デスクトップは横並び、スマホ幅は縦並び。
// - **確定は二段ボタン** (独立した確認ダイアログはやめる): 「9/27 5:07 に戻す」
//   (一段目) → 押すと同じ位置が「本当に戻す / やめる」(二段目) に変わる。
//   スライダーを動かすと一段目に戻る。確定は常に `POST .../generations/:seq
//   ?sessionId=` → `pastCanvasId` → `whiteboard.restore-request` (行そのもの点
//   だけ直接)。
// - `?recover=<seq>` (深リンク): この文書の中で「seq より前の最後の世代」が
//   あればそれを初期選択、無ければ右端 + 案内文 (最新世代へのフォールバックは
//   「無いので既に右端」という意味で自然に起きる)。
//
// - **第 2 段階「人を外して戻す」(2026-10-01)**: 世代一覧の応答が
//   `exclusionAvailable === true` のときだけ、上部にモード切替 (「時点を選んで戻す」/
//   「人を外して戻す」) を出す。それ以外の部屋・利用者には第 1 段階と完全に同じ画面
//   (他の推測はしない)。「人を外して戻す」の本体は `ExclusionRecoveryPanel`。焼き上がった
//   過去キャンバス行は、ここの `sendRestoreRequest` + 待受 (= 第 1 段階と同じ経路) で
//   戻す。復元後の「元に戻す」トーストは `whiteboard.restore` の id から既存経路が出す。
//   `kind: 'excluded'` の行は外した版なので、このモードの対象外 (サーバも拒否する)。
//
// engine / drawing には触れない (Blob 取得は呼び出し元が渡す関数任せ)。

import { useCallback, useEffect, useRef, useState } from 'react';

import { formatPastCanvasDateTime, restoreRejectedText } from '../application/pastCanvasList.js';
import type { Transport } from '../application/ports/Transport.js';
import {
  buildPickerGenerations,
  type PickerGeneration,
  pickInitialGenerationIndex,
  type RecoveryPickerRow,
} from '../application/recoveryGenerations.js';
import { sendRestoreRequest } from '../application/useCases.js';
import { readEnv } from '../infrastructure/env.js';
import { fetchPastCanvasSnapshotUrls } from '../infrastructure/pastCanvas/pastCanvasClient.js';
import { composePastCanvasThumbnail } from '../infrastructure/pastCanvas/pastCanvasThumbnail.js';
import {
  createPastCanvasFromGeneration,
  fetchRecoveryGenerationSnapshotUrls,
  fetchRecoveryGenerations,
} from '../infrastructure/pastCanvas/recoveryGenerationsClient.js';
import { ExclusionRecoveryPanel } from './ExclusionRecoveryPanel.js';
import { PHONE_BODY_EDGE_INSET_CLASS } from './modalChrome.js';

/** 比較プレビューの長辺 (px)。一覧サムネ (240px) より大きく、取り違え対策として視認性を優先する。 */
const PREVIEW_MAX_EDGE = 640;

const LIST_ERROR_MESSAGES = {
  unauthorized: 'この機能を使う権限がありません。オーナーとしてログインしてください',
  'not-found': 'この部屋が見つかりませんでした',
  unavailable: '一時的に利用できません。しばらくしてからもう一度お試しください',
  error: '世代の取得に失敗しました。通信環境を確認して再度お試しください',
} as const;

const CREATE_ERROR_MESSAGES = {
  unauthorized: '復元する権限がありません。オーナーとしてログインし直してください',
  'not-found': 'この時点は選べなくなりました (文書が変わった可能性があります)',
  unavailable: '一時的に利用できません。しばらくしてからもう一度お試しください',
  error: '復元の準備に失敗しました。通信環境を確認して再度お試しください',
} as const;

const RECOVER_MISS_NOTICE = '通知の時点の世代が見つからなかったため、保存時点を表示しています。';

const NO_OP_AT_RIGHT_NOTICE = '戻したい時点までスライダーを動かしてください';

type ListState =
  | { readonly t: 'loading' }
  | { readonly t: 'ok'; readonly generations: readonly PickerGeneration[] }
  | { readonly t: 'empty' }
  | { readonly t: 'error'; readonly message: string };

type PreviewState =
  | { readonly t: 'idle' }
  | { readonly t: 'loading' }
  | { readonly t: 'ready'; readonly url: string }
  | { readonly t: 'placeholder' };

export interface RecoveryGenerationPickerModalProps {
  readonly roomId: string;
  readonly transport: Transport;
  readonly adminToken?: string;
  readonly isMobile?: boolean;
  /** ルームのキャンバス寸法 (第 2 段階のプレビュー replay / 軌跡の重ね描きに使う)。 */
  readonly canvasWidth?: number;
  readonly canvasHeight?: number;
  /** 開いた文書の sessionId。省略 = 現行文書 (「今の絵」カードから開いた)。 */
  readonly sessionId?: string;
  /**
   * 過去キャンバス一覧の行から開いた場合、その行の情報 (入口統一)。省略 =
   * 「今の絵」カードから開いた (上限なし・合成点も無し・右端は確定不可)。
   */
  readonly row?: RecoveryPickerRow;
  /**
   * `?recover=<seq>` 深リンクの初期選択。この文書の中で「seq より前の最後の
   * 世代」が見つかればそれを選ぶ。見つからなければ右端 (既定) のまま案内文を出す。
   */
  readonly initialSeq?: number;
  /** 現在表示中キャンバスの比較用プレビュー (`useRoomChrome.getCurrentCanvasBlob`)。 */
  readonly getCurrentCanvasBlob: () => Promise<Blob | null>;
  /** 現行文書の現在の背景色 (rgb24)。「人を外して戻す」のプレビュー初期背景に使う。 */
  readonly currentBackgroundRgb?: number;
  /** × / キャンセル / Esc で閉じたとき (= 復元は起きていない、呼び出し元の一覧へ戻る)。 */
  readonly onClose: () => void;
  /**
   * 復元 (`whiteboard.restore` broadcast) が成功したとき。省略時は `onClose` と同じ
   * 扱い。親 (`PastCanvasListModal`) はこれに「一覧ごと閉じる」ハンドラを渡し、
   * 単なるキャンセルとは区別する。
   */
  readonly onRestored?: () => void;
}

export const RecoveryGenerationPickerModal = ({
  roomId,
  transport,
  adminToken,
  isMobile = false,
  canvasWidth = 800,
  canvasHeight = 600,
  sessionId,
  row,
  initialSeq,
  getCurrentCanvasBlob,
  currentBackgroundRgb,
  onClose,
  onRestored,
}: RecoveryGenerationPickerModalProps): React.ReactNode => {
  const [listState, setListState] = useState<ListState>({ t: 'loading' });
  // `null` = ユーザ/深リンクがまだ明示的に選んでいない (= 既定の右端を使う)。
  // 「右端が既定」を effect の corrective setState で表現すると、世代一覧が届いた
  // 最初のコミットでは旧値のまま描画され、補正コミットまで 1 拍遅れる (= テストで
  // 見えるチラつき・genPreview の無駄な二重 fetch の原因になる)。ここでは代わりに
  // `effectiveIndex` (下記) を render 時に導出することで、一覧が届いた最初の
  // コミットから既に右端が正しく表示される。
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // `?recover=` がこの文書に見つからなかった (右端 = 既定のままだが理由を示す)。
  const [showRecoverMissNotice, setShowRecoverMissNotice] = useState(false);
  // 二段ボタン (入口統一 §2「確定は二段ボタン」)。スライダーを動かすと一段目に戻す。
  const [confirmArmed, setConfirmArmed] = useState(false);
  // 第 2 段階 (人を外して戻す)。サーバが `exclusionAvailable: true` と返したときだけ true。
  const [exclusionAvailable, setExclusionAvailable] = useState(false);
  const [mode, setMode] = useState<'generation' | 'exclusion'>('generation');

  const [currentPreview, setCurrentPreview] = useState<PreviewState>({ t: 'idle' });
  const [genPreview, setGenPreview] = useState<PreviewState>({ t: 'idle' });
  const genPreviewCache = useRef(new Map<string, string>());

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [restorePending, setRestorePending] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // row の primitive フィールドだけ effect の依存に使う (呼び出し元が毎回新しい
  // object literal を渡しても、値が同じなら再 fetch しない)。
  const rowSnapshotSeq = row?.snapshotSeq;
  const rowPastCanvasId = row?.pastCanvasId;
  const rowClearedAt = row?.clearedAt;

  // ── 世代一覧の取得 (行から開いた場合は行の文書、今の絵なら現行文書)。 ─────────
  useEffect(() => {
    const ac = new AbortController();
    setListState({ t: 'loading' });
    setExclusionAvailable(false);
    setMode('generation');
    void (async () => {
      const out = await fetchRecoveryGenerations({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        ...(sessionId !== undefined ? { sessionId } : {}),
        ...(adminToken !== undefined ? { adminToken } : {}),
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      const rowArg: RecoveryPickerRow | undefined =
        rowSnapshotSeq !== undefined && rowPastCanvasId !== undefined && rowClearedAt !== undefined
          ? { snapshotSeq: rowSnapshotSeq, pastCanvasId: rowPastCanvasId, clearedAt: rowClearedAt }
          : undefined;
      // 第 2 段階の出し分けはこの 1 値だけ (失敗時は常に false)。
      if (out.outcome === 'ok') setExclusionAvailable(out.exclusionAvailable);
      if (out.outcome !== 'ok') {
        if (rowArg !== undefined) {
          // 行から開いた場合は「行そのもの」1 点だけで動く (従来の「続きを描く」と
          // 同じ耐性、世代 API の障害に影響されない)。
          const points = buildPickerGenerations([], rowArg);
          setListState({ t: 'ok', generations: points });
          return;
        }
        setListState({ t: 'error', message: LIST_ERROR_MESSAGES[out.outcome] });
        return;
      }
      const points = buildPickerGenerations(out.generations, rowArg);
      setListState(points.length === 0 ? { t: 'empty' } : { t: 'ok', generations: points });
    })();
    return () => ac.abort();
  }, [roomId, adminToken, sessionId, rowSnapshotSeq, rowPastCanvasId, rowClearedAt]);

  // ── 初期選択: `initialSeq` があればこの文書の中で一致を試み、見つかればそちらを
  // 明示選択する (見つからなければ案内文だけ出す — 既定の右端は `effectiveIndex`
  // が常に導出するので、ここで明示的に設定する必要は無い)。
  useEffect(() => {
    if (listState.t !== 'ok' || initialSeq === undefined) return;
    const idx = pickInitialGenerationIndex(listState.generations, initialSeq);
    if (idx !== null) {
      setSelectedIndex(idx);
      setShowRecoverMissNotice(false);
    } else {
      setShowRecoverMissNotice(true);
    }
  }, [listState, initialSeq]);

  // ── 「今の絵」プレビュー (1 回だけ取得、選んだ時点に関わらず常に「今」)。 ──────
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setCurrentPreview({ t: 'loading' });
    void (async () => {
      const blob = await getCurrentCanvasBlob();
      if (cancelled) return;
      if (blob === null) {
        setCurrentPreview({ t: 'placeholder' });
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setCurrentPreview({ t: 'ready', url: objectUrl });
    })();
    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [getCurrentCanvasBlob]);

  const generations = listState.t === 'ok' ? listState.generations : undefined;
  // 既定は右端 (= 保存時点 / 最新世代)。`selectedIndex` が明示されていればそちら。
  const effectiveIndex = selectedIndex ?? Math.max(0, (generations?.length ?? 1) - 1);
  const selected = generations?.[effectiveIndex];

  // ── 選択世代のプレビュー (選択変更のたびに取得、期限切れ 1 回だけ再署名リトライ)。
  // 合成点 (`isSynthetic`) は世代 API の対象ではないので既存の過去キャンバス
  // `snapshot-urls` (= 行の既知 pastCanvasId) を使う。 ─────────────────────────
  useEffect(() => {
    if (selected === undefined) return;
    const cacheKey = selected.isSynthetic ? 'row' : String(selected.seq);
    const cached = genPreviewCache.current.get(cacheKey);
    if (cached !== undefined) {
      setGenPreview({ t: 'ready', url: cached });
      return;
    }
    const ac = new AbortController();
    setGenPreview({ t: 'loading' });
    void (async () => {
      const dispatcherUrl = readEnv().dispatcherUrl;
      const signOnce = () =>
        selected.isSynthetic && rowPastCanvasId !== undefined
          ? fetchPastCanvasSnapshotUrls({
              dispatcherUrl,
              roomId,
              pastCanvasId: rowPastCanvasId,
              ...(adminToken !== undefined ? { adminToken } : {}),
              signal: ac.signal,
            })
          : fetchRecoveryGenerationSnapshotUrls({
              dispatcherUrl,
              roomId,
              seq: selected.seq,
              ...(sessionId !== undefined ? { sessionId } : {}),
              ...(adminToken !== undefined ? { adminToken } : {}),
              signal: ac.signal,
            });
      let signed = await signOnce();
      if (ac.signal.aborted) return;
      if (signed.outcome !== 'ok') {
        setGenPreview({ t: 'placeholder' });
        return;
      }
      let composed = await composePastCanvasThumbnail(signed.urls, {
        maxEdge: PREVIEW_MAX_EDGE,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (composed.outcome === 'expired') {
        signed = await signOnce();
        if (ac.signal.aborted) return;
        if (signed.outcome !== 'ok') {
          setGenPreview({ t: 'placeholder' });
          return;
        }
        composed = await composePastCanvasThumbnail(signed.urls, {
          maxEdge: PREVIEW_MAX_EDGE,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
      }
      if (composed.outcome !== 'ok') {
        setGenPreview({ t: 'placeholder' });
        return;
      }
      const url = URL.createObjectURL(composed.blob);
      genPreviewCache.current.set(cacheKey, url);
      setGenPreview({ t: 'ready', url });
    })();
    return () => ac.abort();
  }, [roomId, adminToken, sessionId, rowPastCanvasId, selected]);

  // unmount 時に合成済みサムネの object URL を解放する。
  useEffect(
    () => () => {
      for (const url of genPreviewCache.current.values()) URL.revokeObjectURL(url);
    },
    [],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // restore-request 送信中の待受 (PastCanvasListModal の「続きを描く」と同じ流儀:
  // 誰が送った broadcast でも pending を解いて閉じる)。
  useEffect(() => {
    if (!restorePending) return;
    const off = transport.onEvent((ev) => {
      if (ev.t === 'whiteboard.restore-rejected') {
        setRestorePending(false);
        setRestoreError(restoreRejectedText(ev.reason));
        return;
      }
      if (ev.t === 'whiteboard.restore') {
        (onRestored ?? onClose)();
      }
    });
    return off;
  }, [restorePending, transport, onClose, onRestored]);

  // スライダー / 前へ・次への共通コミット。動かしたら二段ボタンを一段目に戻す
  // (入口統一 §2「スライダーを動かしたら一段目に戻す」)。
  const commitIndex = useCallback((idx: number): void => {
    setSelectedIndex(idx);
    setShowRecoverMissNotice(false);
    setConfirmArmed(false);
  }, []);

  const generationCount = generations?.length ?? 0;
  const canGoPrev = generationCount > 0 && effectiveIndex > 0;
  const canGoNext = generationCount > 0 && effectiveIndex < generationCount - 1;
  // 「今の絵」(row 無し) の右端は現行文書の最新世代 = 「今」そのものなので、
  // 確定しても何も変わらない。この間は確定を無効化する (入口統一 §2)。
  const isNoOpAtRight =
    row === undefined && generationCount > 0 && effectiveIndex === generationCount - 1;

  const handleCreateAndRestore = useCallback((): void => {
    if (selected === undefined) return;
    setCreateError(null);
    if (selected.isSynthetic) {
      // 行そのもの点: POST を経由せず、行の既知 pastCanvasId で直接 restore-request。
      if (rowPastCanvasId === undefined) return;
      setRestoreError(null);
      sendRestoreRequest(transport, rowPastCanvasId);
      setRestorePending(true);
      return;
    }
    setCreating(true);
    void (async () => {
      const out = await createPastCanvasFromGeneration({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        seq: selected.seq,
        ...(sessionId !== undefined ? { sessionId } : {}),
        ...(adminToken !== undefined ? { adminToken } : {}),
      });
      setCreating(false);
      if (out.outcome !== 'ok') {
        setCreateError(CREATE_ERROR_MESSAGES[out.outcome]);
        return;
      }
      setRestoreError(null);
      sendRestoreRequest(transport, out.pastCanvasId);
      setRestorePending(true);
    })();
  }, [selected, rowPastCanvasId, roomId, sessionId, adminToken, transport]);

  // 外した版が焼き上がった: 第 1 段階と同じ `restore-request` 経路で戻す。
  const handleBaked = useCallback(
    (pastCanvasId: string): void => {
      setRestoreError(null);
      sendRestoreRequest(transport, pastCanvasId);
      setRestorePending(true);
    },
    [transport],
  );

  // 第 2 段階のモード切替は、サーバが `exclusionAvailable: true` と返した部屋・利用者
  // にだけ出す。外した版 (`kind: 'excluded'`) の行は対象外。
  const showExclusionMode = exclusionAvailable && row?.kind !== 'excluded';
  const exclusionActive = showExclusionMode && mode === 'exclusion';

  const modeTabs: React.ReactNode = showExclusionMode ? (
    <div role="tablist" aria-label="戻し方" className="mb-3 flex gap-1">
      {(
        [
          ['generation', '時点を選んで戻す'],
          ['exclusion', '人を外して戻す'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={mode === key}
          onClick={() => setMode(key)}
          className={`rounded border px-3 py-1.5 text-xs ${
            mode === key
              ? 'border-[var(--color-accent)] bg-[var(--color-accent)] font-bold text-[var(--color-panel)]'
              : 'border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-soft)]'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null;

  const previewBox = (label: string, state: PreviewState): React.ReactNode => (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="select-none text-[11px] text-[var(--color-text-muted)]">{label}</span>
      <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg)]">
        {state.t === 'ready' ? (
          <img src={state.url} alt={label} className="max-h-full max-w-full object-contain" />
        ) : state.t === 'loading' ? (
          <span
            role="status"
            aria-label={`${label}を読み込み中`}
            className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
          />
        ) : (
          <span className="text-[11px] text-[var(--color-text-muted)]">プレビューなし</span>
        )}
      </div>
    </div>
  );

  const body = ((): React.ReactNode => {
    if (listState.t === 'loading') {
      return (
        <p role="status" className="text-sm text-[var(--color-text-muted)]">
          世代を読み込んでいます…
        </p>
      );
    }
    if (listState.t === 'empty') {
      return <p className="text-sm text-[var(--color-text)]">戻せる時点がありません。</p>;
    }
    if (listState.t === 'error') {
      return <p className="text-sm text-[var(--color-text)]">{listState.message}</p>;
    }
    const selectedLabel =
      selected !== undefined && !selected.isRowPoint
        ? `${formatPastCanvasDateTime(selected.savedAt)} の絵`
        : '保存時点の絵';
    return (
      <div className="flex flex-col gap-4">
        {/* 比較プレビュー: デスクトップ横並び / スマホ縦並び (取り違え対策)。 */}
        <div className="flex flex-col gap-3 sm:flex-row">
          {previewBox('今の絵', currentPreview)}
          {previewBox(selectedLabel, genPreview)}
        </div>

        {showRecoverMissNotice && (
          <p role="status" className="text-[var(--color-text-muted)] text-xs">
            {RECOVER_MISS_NOTICE}
          </p>
        )}

        {/* スライダー: 単一ハンドル、常に有効 (タイムラプスと同じ操作感)。 */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => commitIndex(Math.max(0, effectiveIndex - 1))}
            disabled={!canGoPrev}
            aria-label="前の世代"
            className="rounded border border-[var(--color-border)] px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← 前
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(0, generationCount - 1)}
            value={effectiveIndex}
            onChange={(e) => commitIndex(Number.parseInt(e.target.value, 10))}
            disabled={generationCount === 0}
            className="min-w-0 flex-1"
            aria-label="世代を選ぶ"
          />
          <button
            type="button"
            onClick={() => commitIndex(Math.min(generationCount - 1, effectiveIndex + 1))}
            disabled={!canGoNext}
            aria-label="次の世代"
            className="rounded border border-[var(--color-border)] px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          >
            次 →
          </button>
        </div>

        {createError !== null && (
          <p role="alert" className="text-xs text-[var(--color-error)]">
            {createError}
          </p>
        )}
        {restoreError !== null && (
          <p role="alert" className="text-xs text-[var(--color-error)]">
            {restoreError}
          </p>
        )}
        {(creating || restorePending) && (
          <p role="status" className="flex items-center gap-2 text-[var(--color-text)] text-xs">
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
            />
            {creating ? '準備しています…' : '復元しています…'}
          </p>
        )}

        {/* 確定: スライダーから離して配置 (入口統一 §2)。「今の絵」の右端は無効化。 */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-white text-xs hover:bg-[var(--color-bg)]"
          >
            キャンセル
          </button>
          {isNoOpAtRight ? (
            <p className="text-[var(--color-text-muted)] text-xs">{NO_OP_AT_RIGHT_NOTICE}</p>
          ) : confirmArmed ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmArmed(false)}
                className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-white text-xs hover:bg-[var(--color-bg)]"
              >
                やめる
              </button>
              <button
                type="button"
                onClick={handleCreateAndRestore}
                disabled={creating || restorePending}
                className="rounded bg-[var(--color-error)] px-3 py-1.5 font-bold text-white text-xs hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                本当に戻す
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmArmed(true)}
              disabled={selected === undefined || creating || restorePending}
              className="rounded bg-[var(--color-accent)] px-3 py-1.5 font-bold text-[var(--color-panel)] text-xs hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {selected !== undefined && !selected.isRowPoint
                ? `${formatPastCanvasDateTime(selected.savedAt)} に戻す`
                : '保存時点に戻す'}
            </button>
          )}
        </div>
      </div>
    );
  })();

  const dialogWidthClass = isMobile
    ? 'w-full'
    : exclusionActive
      ? 'w-[960px] max-w-full'
      : 'w-[720px] max-w-full';

  return (
    <div
      className={isMobile ? 'fixed inset-0 z-50' : 'fixed inset-0 z-50 grid place-items-center p-4'}
    >
      {!isMobile && (
        <button
          type="button"
          aria-label="閉じる"
          tabIndex={-1}
          onClick={onClose}
          className="-z-10 absolute inset-0 cursor-default bg-black/60"
        />
      )}
      <div
        role="dialog"
        aria-modal="true"
        className={
          isMobile
            ? `flex h-[100dvh] w-full flex-col overflow-y-auto border border-white/10 bg-[var(--color-panel)] shadow-xl [padding-top:env(safe-area-inset-top)] ${PHONE_BODY_EDGE_INSET_CLASS}`
            : `max-h-[90vh] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-5 shadow-xl ${dialogWidthClass}`
        }
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-bold text-[var(--color-accent)] text-base">続きを描く</h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
          >
            ×
          </button>
        </div>
        {modeTabs}
        {exclusionActive ? (
          <>
            <ExclusionRecoveryPanel
              roomId={roomId}
              transport={transport}
              {...(adminToken !== undefined ? { adminToken } : {})}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              {...(row !== undefined ? { row } : {})}
              {...(currentBackgroundRgb !== undefined ? { currentBackgroundRgb } : {})}
              currentPreviewUrl={currentPreview.t === 'ready' ? currentPreview.url : null}
              restoring={restorePending}
              onBaked={handleBaked}
              onCancel={onClose}
            />
            {restoreError !== null && (
              <p role="alert" className="mt-2 text-xs text-[var(--color-error)]">
                {restoreError}
              </p>
            )}
          </>
        ) : (
          body
        )}
      </div>
    </div>
  );
};
